import React, { useState } from "react";
import { Phone, Send, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { COMPANY, EMAILS } from "@/lib/content";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import EmailStrip from "@/components/EmailStrip";

export default function ContactPage() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name:"", email:"", phone:"", message:"" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="vj-dark vj-radial-fade px-6 lg:px-12 py-20">
        <div className="max-w-[1400px]">
          <div className="overline text-slate-400">Contact Us</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter leading-[1.05] mt-4 max-w-4xl">
            Talk to our engineering & business team.
          </h1>
          <p className="text-slate-300 mt-6 max-w-2xl leading-relaxed">
            Reach out about defence and industrial programs, partnerships, internships, or technology engagements.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16 max-w-[1400px] grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-6">
          <div className="vj-card p-7" data-testid="contact-phone-card">
            <div className="flex items-center gap-2 overline"><Phone size={12}/> Primary Contact</div>
            <div className="font-display text-3xl font-extrabold mt-3 tracking-tight">{COMPANY.phone}</div>
            <p className="text-sm text-slate-500 mt-2">Available for business, defence, and partnership enquiries.</p>
          </div>
          <div className="vj-card p-7">
            <div className="flex items-center gap-2 overline"><MapPin size={12}/> Operations</div>
            <div className="font-display text-xl font-bold mt-3">India · Global Operations</div>
            <p className="text-sm text-slate-500 mt-2">Headquartered in India · serving global customers across defence, government, and enterprise sectors.</p>
          </div>
          <div className="vj-card p-7" data-testid="contact-email-card">
            <div className="flex items-center gap-2 overline"><Mail size={12}/> Email</div>
            <div className="mt-4 space-y-3">
              <a href={`mailto:${EMAILS.general}`} className="block group" data-testid="contact-email-general">
                <div className="overline">General</div>
                <div className="text-sm font-semibold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors">{EMAILS.general}</div>
              </a>
              <a href={`mailto:${EMAILS.careers}`} className="block group" data-testid="contact-email-careers">
                <div className="overline">Careers</div>
                <div className="text-sm font-semibold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors">{EMAILS.careers}</div>
              </a>
              <a href={`mailto:${EMAILS.support}`} className="block group" data-testid="contact-email-support">
                <div className="overline">Support</div>
                <div className="text-sm font-semibold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors">{EMAILS.support}</div>
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="vj-card p-7 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
            <div className="md:col-span-2">
              <div className="overline">Send a Message</div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight mt-1">Contact Form</h2>
            </div>
            <Field label="Name" value={form.name} onChange={onChange("name")} required testid="contact-name"/>
            <Field label="Email" type="email" value={form.email} onChange={onChange("email")} required testid="contact-email"/>
            <Field label="Phone" value={form.phone} onChange={onChange("phone")} testid="contact-phone-input" className="md:col-span-2"/>
            <div className="md:col-span-2">
              <label className="overline">Message</label>
              <textarea data-testid="contact-message" required value={form.message} onChange={onChange("message")} className="vj-input mt-1.5" placeholder="Tell us about your enquiry or program."/>
            </div>
            {err && <div className="md:col-span-2 text-sm text-red-600">{err}</div>}
            <div className="md:col-span-2">
              <button disabled={busy} className="vj-btn-primary" data-testid="contact-submit">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Send Message
              </button>
            </div>
          </form>
        </div>
      </section>

      <EmailStrip
        heading="Prefer email? Our support team is here to help."
        emails={[{ label: "Support", address: EMAILS.support }]}
      />
    </div>
  );
}

function Field({ label, testid, className = "", ...props }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input data-testid={testid} className="vj-input mt-1.5" {...props}/>
    </div>
  );
}
